import React from "react";
import { CenteredOverlay } from "../../components/common/CenteredOverlay";
import Overlay from "../../components/common/Overlay";
import { TimeLoading } from "../../components/icons/TimeLoading";
import { Warning } from "../../components/icons";
import {
  ButtonsWrapper,
  ModalContainer,
  ModalTitle,
} from "../../common/modal.styles";
import Button from "../../components/common/Button";
import { InfoModal } from "./InfoModal";
import { useReservationResources } from "./hooks/useReservationResources";

interface ReservationModalsProps {
  expirationModalOpen: boolean;
  onCloseExpiration: () => void;

  isProductsUpdatedModalOpen?: boolean;
  onCloseProductsUpdated?: () => void;

  isProductMinQuantityModalOpen?: boolean;
  onCloseProductMinQuantity?: () => void;
  productMinQuantityDescription?: string;

  isEmailLimitModalOpen?: boolean;
  onCloseEmailLimit?: () => void;
  emailLimitDescription?: string;
  /** The email still has allowance left but the basket is over it ("exceeded", not "reached"). */
  emailLimitExceeded?: boolean;

  isLeaveModalOpen?: boolean;
  onCloseLeaveModal?: () => void;
  onLeave?: () => void;

  isGenericErrorModalOpen: boolean;
  onCloseGenericError: () => void;
}

export function ReservationModals({
  expirationModalOpen,
  onCloseExpiration,
  isProductsUpdatedModalOpen,
  onCloseProductsUpdated,
  isProductMinQuantityModalOpen,
  onCloseProductMinQuantity,
  productMinQuantityDescription,
  isEmailLimitModalOpen,
  onCloseEmailLimit,
  emailLimitDescription,
  emailLimitExceeded,
  isLeaveModalOpen,
  onCloseLeaveModal,
  onLeave,
  isGenericErrorModalOpen,
  onCloseGenericError,
}: ReservationModalsProps) {
  const r = useReservationResources();

  return (
    <>
      <CenteredOverlay
        handleClose={onCloseExpiration}
        open={expirationModalOpen}
        size="sm"
        id="expiration"
        titleText={r.ReservationExpiredTitle}
        icon={<TimeLoading />}
      >
        <InfoModal
          description={r.ReservationExpiredDescription}
          buttonText={r.StartNewReservation}
          onClick={onCloseExpiration}
        />
      </CenteredOverlay>

      {isProductsUpdatedModalOpen !== undefined && onCloseProductsUpdated && (
        <CenteredOverlay
          handleClose={onCloseProductsUpdated}
          open={isProductsUpdatedModalOpen}
          size="sm"
          id="product-removed"
          titleText={r.ReservationUpdatedTitle}
          icon={<Warning />}
        >
          <InfoModal
            description={r.ReservationUpdatedDescription}
            buttonText={r.ContinueReservation}
            onClick={onCloseProductsUpdated}
          />
        </CenteredOverlay>
      )}

      {isProductMinQuantityModalOpen !== undefined && onCloseProductMinQuantity && (
        <CenteredOverlay
          handleClose={onCloseProductMinQuantity}
          open={isProductMinQuantityModalOpen}
          size="sm"
          id="product-min-quantity"
          titleText={r.ProductMinimumQuantityNotMetTitle}
          icon={<Warning />}
        >
          <InfoModal
            description={productMinQuantityDescription ?? ""}
            buttonText={r.ContinueReservation}
            onClick={onCloseProductMinQuantity}
          />
        </CenteredOverlay>
      )}

      {isEmailLimitModalOpen !== undefined && onCloseEmailLimit && (
        <CenteredOverlay
          handleClose={onCloseEmailLimit}
          open={isEmailLimitModalOpen}
          size="sm"
          id="email-limit-reached"
          titleText={
            emailLimitExceeded
              ? r.EmailTicketLimitExceededTitle
              : r.EmailTicketLimitReachedTitle
          }
          icon={<Warning />}
        >
          <InfoModal
            description={emailLimitDescription ?? ""}
            buttonText={r.GotIt}
            onClick={onCloseEmailLimit}
          />
        </CenteredOverlay>
      )}

      {onCloseLeaveModal && onLeave && (
        <Overlay
          handleClose={onCloseLeaveModal}
          open={isLeaveModalOpen ?? false}
          size="sm"
          id="leave-reservation"
          titleText={r.LeaveReservationTitle}
        >
          <ModalContainer>
            <ModalTitle>{r.LeaveReservationDescription}</ModalTitle>
            <ButtonsWrapper>
              <Button
                id="yes-btn"
                type="button"
                variant="secondary"
                height="48px"
                text={r.Yes}
                onClick={onLeave}
                fullWidth
              />
              <Button
                id="no-btn"
                type="button"
                variant="primary"
                height="48px"
                text={r.No}
                onClick={onCloseLeaveModal}
                fullWidth
              />
            </ButtonsWrapper>
          </ModalContainer>
        </Overlay>
      )}

      <CenteredOverlay
        handleClose={onCloseGenericError}
        open={isGenericErrorModalOpen}
        size="sm"
        id="generic-error"
        titleText={r.SomethingWrong}
        icon={<Warning color="#FC2D33" />}
      >
        <InfoModal
          description={r.SomethingWrongText}
          buttonText={r.TryAgain}
          onClick={onCloseGenericError}
        />
      </CenteredOverlay>
    </>
  );
}
